/**
 * Foundry Agent Service
 *
 * API client for Azure AI Foundry agent operations.
 * Uses the Assistants-compatible REST API exposed by Foundry projects.
 *
 * Endpoint: https://{accountName}.services.ai.azure.com/api/projects/{projectName}
 * Auth: Azure AD bearer token with audience https://ai.azure.com/
 *
 * Follows the same patterns as azureResourceService.ts:
 *   - Native fetch with AbortController timeout
 *   - Bearer token auth passed as parameter
 *   - Multi-level error parsing
 *   - Cursor-based pagination (OpenAI list style)
 */

// =============================================================================
// TYPES
// =============================================================================

/** Tool definition attached to a Foundry agent */
export interface FoundryToolDefinition {
  type: 'code_interpreter' | 'file_search' | 'function';
  function?: {
    name: string;
    description?: string;
    parameters?: Record<string, unknown>;
  };
}

/** Raw agent version definition from the Foundry API */
interface FoundryAgentVersionDefinition {
  kind?: string;
  model?: string;
  instructions?: string;
  tools?: FoundryToolDefinition[];
}

/** Raw agent response from Foundry API */
interface FoundryAgentRaw {
  object: 'agent';
  id: string;
  name: string;
  versions?: {
    latest?: {
      metadata?: Record<string, string>;
      id?: string;
      name?: string;
      version?: string;
      description?: string;
      created_at?: number;
      definition?: FoundryAgentVersionDefinition;
    };
  };
}

/** Foundry agent version: v1 uses /assistants API, v2 uses /agents API */
export type FoundryAgentVersion = 'v1' | 'v2';

/** Normalized Foundry agent (used throughout the UI) */
export interface FoundryAgent {
  /** Unique agent identifier */
  id: string;
  /** Human-readable agent name */
  name: string | null;
  /** Model deployment used by the agent */
  model: string;
  /** System instructions for the agent */
  instructions: string | null;
  /** Tools attached to the agent */
  tools: FoundryToolDefinition[];
  /** User-defined metadata key-value pairs */
  metadata: Record<string, string>;
  /** Unix timestamp (seconds) when the agent was created */
  created_at: number;
  /** Object type */
  object: 'agent';
  /** Agent version — v1 (/assistants) or v2 (/agents) */
  agentVersion: FoundryAgentVersion;
  /** Agent description (available in v1 assistants) */
  description: string | null;
  /** Selected Foundry definition version for v2 agents */
  version: string | null;
}

/** A single version of a Foundry v2 agent definition. */
export interface FoundryAgentDefinitionVersion {
  id: string;
  name: string;
  version: string;
  created_at: number;
  description: string | null;
  definition: {
    model: string;
    instructions: string | null;
    tools: FoundryToolDefinition[];
  };
}

interface FoundryAgentVersionsResponse {
  object: 'list';
  data: Array<{
    id: string;
    name: string;
    version: string;
    created_at?: number;
    description?: string;
    definition?: FoundryAgentVersionDefinition;
  }>;
  first_id: string | null;
  last_id: string | null;
  has_more: boolean;
}

interface FoundryAgentVersionsResult {
  data: FoundryAgentVersionsResponse['data'];
  last_id?: string | null;
  has_more?: boolean;
}

function extractAgentVersionsResult(raw: unknown): FoundryAgentVersionsResult {
  if (Array.isArray(raw)) {
    return {
      data: raw as FoundryAgentVersionsResponse['data'],
      has_more: false,
    };
  }
  if (!raw || typeof raw !== 'object') {
    return { data: [], has_more: false };
  }

  const record = raw as Record<string, unknown>;
  const nested = record.result;
  const source =
    nested && typeof nested === 'object'
      ? (nested as Record<string, unknown>)
      : record;
  return {
    data: Array.isArray(source.data)
      ? (source.data as FoundryAgentVersionsResponse['data'])
      : [],
    last_id: typeof source.last_id === 'string' ? source.last_id : null,
    has_more: source.has_more === true,
  };
}

/** Raw assistant response from Foundry v1 API (/assistants) — flat structure */
interface FoundryAssistantRaw {
  object: 'assistant';
  id: string;
  name: string | null;
  description: string | null;
  model: string;
  instructions: string | null;
  tools: FoundryToolDefinition[];
  top_p: number;
  temperature: number;
  tool_resources: Record<string, unknown>;
  metadata: Record<string, string>;
  created_at: number;
  response_format: string | unknown;
}

/** Normalize a raw Foundry v2 agent response to our internal type */
function normalizeAgent(raw: FoundryAgentRaw): FoundryAgent {
  const latest = raw.versions?.latest;
  const def = latest?.definition;
  return {
    id: raw.id,
    name: raw.name ?? null,
    model: def?.model ?? '',
    instructions: def?.instructions ?? null,
    tools: def?.tools ?? [],
    metadata: latest?.metadata ?? {},
    created_at: latest?.created_at ?? 0,
    object: 'agent',
    agentVersion: 'v2',
    description: latest?.description ?? null,
    version: latest?.version ?? null,
  };
}

/** Normalize a raw Foundry v1 assistant response to our internal type */
function normalizeAssistant(raw: FoundryAssistantRaw): FoundryAgent {
  return {
    id: raw.id,
    name: raw.name ?? null,
    model: raw.model ?? '',
    instructions: raw.instructions ?? null,
    tools: raw.tools ?? [],
    metadata: raw.metadata ?? {},
    created_at: raw.created_at ?? 0,
    object: 'agent',
    agentVersion: 'v1',
    description: raw.description ?? null,
    version: null,
  };
}

/** Paginated list response from Foundry API (v2 agents) */
export interface FoundryAgentListResponse {
  object: 'list';
  data: FoundryAgentRaw[];
  first_id: string | null;
  last_id: string | null;
  has_more: boolean;
}

/** Paginated list response from Foundry API (v1 assistants) */
export interface FoundryAssistantListResponse {
  object: 'list';
  data: FoundryAssistantRaw[];
  first_id: string | null;
  last_id: string | null;
  has_more: boolean;
}

/** Options for listing agents */
export interface ListAgentsOptions {
  /** Maximum number of agents to return (1–100, default 20) */
  limit?: number;
  /** Sort order by created_at: 'asc' or 'desc' */
  order?: 'asc' | 'desc';
  /** Cursor for pagination — return agents after this ID */
  after?: string | undefined;
  /** Cursor for pagination — return agents before this ID */
  before?: string | undefined;
}

/** Payload for updating an existing agent */
export interface UpdateAgentPayload {
  /** Model deployment name */
  model?: string;
  /** Human-readable agent name */
  name?: string | null;
  /** System instructions */
  instructions?: string | null;
  /** Tools to attach (replaces existing tools) */
  tools?: FoundryToolDefinition[];
  /** User-defined metadata */
  metadata?: Record<string, string>;
}

/** Payload for creating a new agent */
export interface CreateAgentPayload {
  /** Model deployment name */
  model: string;
  /** Human-readable agent name */
  name: string;
  /** System instructions */
  instructions?: string;
  /** Tools to attach */
  tools?: FoundryToolDefinition[];
  /** User-defined metadata */
  metadata?: Record<string, string>;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const REQUEST_TIMEOUT = 30_000; // 30 seconds
const FOUNDRY_API_VERSION = '2025-05-15-preview';
const FOUNDRY_AGENT_VERSIONS_API_VERSION = '2025-11-15-preview';

// =============================================================================
// PORTAL URL BUILDER
// =============================================================================

/**
 * Build a deep-link URL to the AI Foundry portal for an agent.
 *
 * Pattern:
 *   https://ai.azure.com/nextgen/r/{encodedSub},{resourceGroup},,{accountName},{projectName}/build/agents/{agentName}/build?version=2
 *
 * @param connectionResourceId - ARM resource ID of the cognitive services account
 * @param projectEndpoint      - Resolved Foundry project endpoint (contains account + project names)
 * @param agentName            - Display name of the agent
 * @param agentVersion         - Agent version (v1 or v2), affects the portal URL version param
 * @param tenantId             - Active tenant used to open Foundry in the correct directory
 * @param v2Page               - V2 agent page to open
 * @param traceId              - W3C trace id to deep link to on the traces page
 */
export function buildFoundryPortalUrl(
  connectionResourceId: string | undefined,
  projectEndpoint: string | undefined,
  agentName: string | undefined,
  agentVersion: FoundryAgentVersion = 'v2',
  tenantId?: string,
  /** Which page to link to for v2 agents: 'build' (designer) or 'traces' (monitoring) */
  v2Page: 'build' | 'traces' = 'build',
  traceId?: string,
): string | undefined {
  if (!connectionResourceId || !projectEndpoint || !agentName) return undefined;

  // Parse subscription, resource group, and account from the ARM resource ID
  const parts = connectionResourceId.split('/');
  let subscriptionId: string | undefined;
  let resourceGroup: string | undefined;
  let accountName: string | undefined;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]?.toLowerCase();
    if (key === 'subscriptions') subscriptionId = parts[i + 1];
    else if (key === 'resourcegroups') resourceGroup = parts[i + 1];
    else if (key === 'accounts') accountName = parts[i + 1];
  }

  // Extract project name from the endpoint URL
  // e.g. https://account.services.ai.azure.com/api/projects/myproject
  const projectMatch = projectEndpoint.match(/\/api\/projects\/([^/?]+)/i);
  const projectName = projectMatch?.[1];

  if (!subscriptionId || !resourceGroup || !accountName || !projectName) return undefined;

  // v1 assistants use the /resource/agentsList portal page with the project ARM ID
  if (agentVersion === 'v1') {
    const projectArmId = `${connectionResourceId}/projects/${projectName}`;
    let url = `https://ai.azure.com/resource/agentsList?wsid=${encodeURIComponent(projectArmId)}`;
    if (tenantId) {
      url += `&tid=${encodeURIComponent(tenantId)}`;
    }
    url += '#Threads';
    return url;
  }

  // Foundry encodes the subscription GUID's 16 raw bytes, not its text form.
  const subscriptionHex = subscriptionId.replace(/-/g, '');
  if (!/^[0-9a-f]{32}$/i.test(subscriptionHex)) return undefined;
  const subscriptionBytes = subscriptionHex
    .match(/.{2}/g)!
    .map((pair) => Number.parseInt(pair, 16));
  const encodedSub = btoa(String.fromCharCode(...subscriptionBytes))
    .replace(/=+$/, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

  // Foundry adopts the caller's W3C trace id, so the runtime records the id it
  // sent and we can address the exact trace row: /traces/{traceId}. Anything that
  // isn't a well-formed W3C trace id (32 lowercase hex, not all zeroes) falls back
  // to the list rather than producing a route Foundry will reject.
  const isTraceId = typeof traceId === 'string' && /^(?!0{32}$)[0-9a-f]{32}$/.test(traceId);
  const tracePath = v2Page === 'traces' && isTraceId
    ? `/traces/${traceId}`
    : `/${v2Page}`;

  const url =
    `https://ai.azure.com/nextgen/r/${encodedSub},${resourceGroup},,${accountName},${projectName}` +
    `/build/agents/${encodeURIComponent(agentName)}${tracePath}`;
  const query = new URLSearchParams();
  if (tenantId) {
    query.set('tid', tenantId);
  }
  const queryString = query.toString();
  return queryString ? `${url}?${queryString}` : url;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Make an authenticated request to the Foundry Agent API.
 * In development, routes through a Vite proxy to avoid CORS issues.
 */
async function foundryRequest<T>(
  accessToken: string,
  method: 'GET' | 'POST' | 'DELETE',
  url: string,
  body?: unknown
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const isDev = import.meta.env.DEV && import.meta.env.MODE !== 'test';

    // In dev mode, proxy through Vite to avoid CORS
    const requestUrl = isDev ? '/foundry-proxy' : url;

    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    };

    if (isDev) {
      headers['X-Foundry-Target'] = url;
    }

    const options: RequestInit = {
      method,
      headers,
      signal: controller.signal,
    };

    if (body !== undefined) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(requestUrl, options);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `${response.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error?.message ?? errorJson.message ?? errorText;
      } catch {
        errorMessage = errorText || response.statusText;
      }
      throw new Error(`Foundry API error: ${errorMessage}`);
    }

    return response.json();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Foundry API request timed out. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Build the base URL for a Foundry project's agent API.
 *
 * @param projectEndpoint - Full project endpoint, e.g.
 *   "https://myaccount.services.ai.azure.com/api/projects/myproject"
 *   or just "https://myaccount.services.ai.azure.com" (we append the path).
 *   Also handles legacy cognitiveservices.azure.com domains.
 */
function buildAgentsUrl(projectEndpoint: string): string {
  let base = projectEndpoint.replace(/\/+$/, '');

  // Transform legacy cognitive services domain to Foundry services domain
  if (base.includes('.cognitiveservices.azure.com')) {
    base = base.replace('.cognitiveservices.azure.com', '.services.ai.azure.com');
  }

  return `${base}/agents?api-version=${FOUNDRY_API_VERSION}`;
}

/**
 * Build the base URL for a Foundry project's v1 assistants API.
 * Handles both AI Foundry project endpoints and standalone Azure OpenAI account endpoints.
 *  - Project: {base}/assistants?api-version=...
 *  - OpenAI account: {base}/openai/assistants?api-version=...
 */
function buildAssistantsUrl(projectEndpoint: string): string {
  let base = projectEndpoint.replace(/\/+$/, '');

  if (base.includes('.cognitiveservices.azure.com')) {
    base = base.replace('.cognitiveservices.azure.com', '.services.ai.azure.com');
  }

  // Standalone Azure OpenAI accounts (no /api/projects/ in path) use /openai/assistants
  if (base.includes('.openai.azure.com') && !base.includes('/api/projects/')) {
    return `${base}/openai/assistants?api-version=${FOUNDRY_API_VERSION}`;
  }

  return `${base}/assistants?api-version=${FOUNDRY_API_VERSION}`;
}

/**
 * Normalize a project endpoint — strip trailing slashes and fix legacy domains.
 */
function normalizeEndpoint(projectEndpoint: string): string {
  let base = projectEndpoint.replace(/\/+$/, '');
  if (base.includes('.cognitiveservices.azure.com')) {
    base = base.replace('.cognitiveservices.azure.com', '.services.ai.azure.com');
  }
  return base;
}

/**
 * Build the assistants path prefix for single-agent operations.
 * OpenAI accounts use /openai/assistants, Foundry projects use /assistants.
 */
function assistantsPathPrefix(projectEndpoint: string): string {
  const base = normalizeEndpoint(projectEndpoint);
  if (base.includes('.openai.azure.com') && !base.includes('/api/projects/')) {
    return `${base}/openai/assistants`;
  }
  return `${base}/assistants`;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * List agents in a Foundry project.
 *
 * @param projectEndpoint - Foundry project endpoint URL
 * @param accessToken     - Azure AD bearer token
 * @param options         - Pagination / sort options
 * @returns Paginated list of agents
 */
export async function listFoundryAgents(
  projectEndpoint: string,
  accessToken: string,
  options?: ListAgentsOptions
): Promise<FoundryAgentListResponse> {
  const url = new URL(buildAgentsUrl(projectEndpoint));

  if (options?.limit) url.searchParams.set('limit', String(options.limit));
  if (options?.order) url.searchParams.set('order', options.order);
  if (options?.after) url.searchParams.set('after', options.after);
  if (options?.before) url.searchParams.set('before', options.before);

  return foundryRequest<FoundryAgentListResponse>(accessToken, 'GET', url.toString());
}

/**
 * List ALL agents in a Foundry project (auto-paginate).
 * Useful when populating a dropdown.
 */
export async function listAllFoundryAgents(
  projectEndpoint: string,
  accessToken: string
): Promise<FoundryAgent[]> {
  const agents: FoundryAgent[] = [];
  let after: string | undefined;

  while (true) {
    const page = await listFoundryAgents(projectEndpoint, accessToken, {
      limit: 100,
      after,
    });

    // Normalize raw agent responses to our internal type
    agents.push(...page.data.map(normalizeAgent));

    if (!page.has_more || !page.last_id) break;
    after = page.last_id;
  }

  return agents;
}

/**
 * Get a single Foundry agent by ID.
 */
export async function getFoundryAgent(
  projectEndpoint: string,
  agentId: string,
  accessToken: string
): Promise<FoundryAgent> {
  const base = projectEndpoint.replace(/\/+$/, '');
  const url = `${base}/agents/${encodeURIComponent(agentId)}?api-version=${FOUNDRY_API_VERSION}`;

  const raw = await foundryRequest<FoundryAgentRaw>(accessToken, 'GET', url);
  return normalizeAgent(raw);
}

/**
 * List all definition versions for a Foundry v2 agent.
 */
export async function listFoundryAgentVersions(
  projectEndpoint: string,
  agentId: string,
  accessToken: string
): Promise<FoundryAgentDefinitionVersion[]> {
  const base = projectEndpoint.replace(/\/+$/, '');
  const versions: FoundryAgentDefinitionVersion[] = [];
  let after: string | undefined;

  while (true) {
    const url = new URL(
      `${base}/agents/${encodeURIComponent(agentId)}/versions`
    );
    url.searchParams.set('api-version', FOUNDRY_AGENT_VERSIONS_API_VERSION);
    url.searchParams.set('limit', '100');
    if (after) url.searchParams.set('after', after);

    const rawResponse = await foundryRequest<unknown>(
      accessToken,
      'GET',
      url.toString()
    );
    const response = extractAgentVersionsResult(rawResponse);
    versions.push(...response.data.map((item) => ({
      id: item.id,
      name: item.name,
      version: item.version,
      created_at: item.created_at ?? 0,
      description: item.description ?? null,
      definition: {
        model: item.definition?.model ?? '',
        instructions: item.definition?.instructions ?? null,
        tools: item.definition?.tools ?? [],
      },
    })));

    if (!response.has_more || !response.last_id) break;
    after = response.last_id;
  }

  return versions.sort((a, b) => {
    const numA = Number(a.version);
    const numB = Number(b.version);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      return numB - numA;
    }
    // Fall back to creation time (newest first), then a string compare, for
    // non-numeric version identifiers.
    if (a.created_at !== b.created_at) {
      return b.created_at - a.created_at;
    }
    return b.version.localeCompare(a.version);
  });
}

/**
 * Create a new agent in a Foundry project.
 *
 * @param projectEndpoint - Foundry project endpoint URL
 * @param payload         - Agent creation parameters
 * @param accessToken     - Azure AD bearer token
 * @returns The newly created agent
 */
export async function createFoundryAgent(
  projectEndpoint: string,
  payload: CreateAgentPayload,
  accessToken: string
): Promise<FoundryAgent> {
  const url = buildAgentsUrl(projectEndpoint);
  const { name, model, instructions, tools, metadata } = payload;
  const body = {
    name,
    ...(metadata ? { metadata } : {}),
    definition: {
      kind: 'prompt',
      model,
      ...(instructions !== undefined ? { instructions } : {}),
      ...(tools !== undefined ? { tools } : {}),
    },
  };
  const raw = await foundryRequest<FoundryAgentRaw>(accessToken, 'POST', url, body);
  return normalizeAgent(raw);
}

/**
 * Update an existing Foundry agent.
 */
export async function updateFoundryAgent(
  projectEndpoint: string,
  agentId: string,
  payload: UpdateAgentPayload,
  accessToken: string
): Promise<FoundryAgent> {
  const base = projectEndpoint.replace(/\/+$/, '');
  const url = `${base}/agents/${encodeURIComponent(agentId)}?api-version=${FOUNDRY_API_VERSION}`;
  const { model, instructions, tools, metadata } = payload;
  const body = {
    ...(metadata ? { metadata } : {}),
    definition: {
      kind: 'prompt',
      ...(model !== undefined ? { model } : {}),
      ...(instructions !== undefined ? { instructions } : {}),
      ...(tools !== undefined ? { tools } : {}),
    },
  };
  const raw = await foundryRequest<FoundryAgentRaw>(accessToken, 'POST', url, body);
  return normalizeAgent(raw);
}

/**
 * Delete a Foundry agent.
 */
export async function deleteFoundryAgent(
  projectEndpoint: string,
  agentId: string,
  accessToken: string
): Promise<{ id: string; deleted: boolean }> {
  const base = projectEndpoint.replace(/\/+$/, '');
  const url = `${base}/agents/${encodeURIComponent(agentId)}?api-version=${FOUNDRY_API_VERSION}`;
  return foundryRequest<{ id: string; deleted: boolean }>(accessToken, 'DELETE', url);
}

// =============================================================================
// V1 ASSISTANTS API FUNCTIONS
// =============================================================================

/**
 * List v1 assistants in a Foundry project.
 * Uses the /assistants endpoint (OpenAI-compatible flat response).
 */
export async function listFoundryAssistants(
  projectEndpoint: string,
  accessToken: string,
  options?: ListAgentsOptions
): Promise<FoundryAssistantListResponse> {
  const url = new URL(buildAssistantsUrl(projectEndpoint));

  if (options?.limit) url.searchParams.set('limit', String(options.limit));
  if (options?.order) url.searchParams.set('order', options.order);
  if (options?.after) url.searchParams.set('after', options.after);
  if (options?.before) url.searchParams.set('before', options.before);

  return foundryRequest<FoundryAssistantListResponse>(accessToken, 'GET', url.toString());
}

/**
 * List ALL v1 assistants in a Foundry project (auto-paginate).
 */
export async function listAllFoundryAssistants(
  projectEndpoint: string,
  accessToken: string
): Promise<FoundryAgent[]> {
  const agents: FoundryAgent[] = [];
  let after: string | undefined;

  while (true) {
    const page = await listFoundryAssistants(projectEndpoint, accessToken, {
      limit: 100,
      after,
    });

    agents.push(...page.data.map(normalizeAssistant));

    if (!page.has_more || !page.last_id) break;
    after = page.last_id;
  }

  return agents;
}

/**
 * Get a single v1 assistant by ID.
 */
export async function getFoundryAssistant(
  projectEndpoint: string,
  agentId: string,
  accessToken: string
): Promise<FoundryAgent> {
  const prefix = assistantsPathPrefix(projectEndpoint);
  const url = `${prefix}/${encodeURIComponent(agentId)}?api-version=${FOUNDRY_API_VERSION}`;

  const raw = await foundryRequest<FoundryAssistantRaw>(accessToken, 'GET', url);
  return normalizeAssistant(raw);
}

/**
 * Update an existing v1 assistant.
 */
export async function updateFoundryAssistant(
  projectEndpoint: string,
  agentId: string,
  payload: UpdateAgentPayload,
  accessToken: string
): Promise<FoundryAgent> {
  const prefix = assistantsPathPrefix(projectEndpoint);
  const url = `${prefix}/${encodeURIComponent(agentId)}?api-version=${FOUNDRY_API_VERSION}`;
  const raw = await foundryRequest<FoundryAssistantRaw>(accessToken, 'POST', url, payload);
  return normalizeAssistant(raw);
}

// =============================================================================
// COMBINED API FUNCTIONS (BOTH VERSIONS)
// =============================================================================

/**
 * List ALL agents from both v1 (/assistants) and v2 (/agents) endpoints.
 * Fetches in parallel and merges results. Each agent is tagged with its version.
 * If one version fails (e.g. project doesn't support it), returns the other.
 */
export async function listAllFoundryAgentsBoth(
  projectEndpoint: string,
  accessToken: string
): Promise<FoundryAgent[]> {
  const isOpenAIAccount = projectEndpoint.includes('.openai.azure.com') && !projectEndpoint.includes('/api/projects/');

  // For standalone Azure OpenAI accounts, only fetch v1 assistants (no v2 agents)
  if (isOpenAIAccount) {
    return listAllFoundryAssistants(projectEndpoint, accessToken);
  }

  const [v2Result, v1Result] = await Promise.allSettled([
    listAllFoundryAgents(projectEndpoint, accessToken),
    listAllFoundryAssistants(projectEndpoint, accessToken),
  ]);

  const seenIds = new Set<string>();
  const seenNameVersion = new Set<string>();
  const agents: FoundryAgent[] = [];

  function addAgent(agent: FoundryAgent): void {
    if (seenIds.has(agent.id)) return;
    // Deduplicate by name+version — v1 assistants are resource-scoped so the
    // same assistant can appear with different IDs across project endpoints.
    const nameKey = `${agent.name ?? ''}::${agent.agentVersion}`;
    if (seenNameVersion.has(nameKey)) return;
    seenIds.add(agent.id);
    seenNameVersion.add(nameKey);
    agents.push(agent);
  }

  // Add v2 agents first (preferred)
  if (v2Result.status === 'fulfilled') {
    for (const agent of v2Result.value) {
      addAgent(agent);
    }
  }

  // Add v1 assistants, skipping duplicates by ID or name+version
  if (v1Result.status === 'fulfilled') {
    for (const assistant of v1Result.value) {
      addAgent(assistant);
    }
  }

  // If both failed, throw the v2 error (primary)
  if (v2Result.status === 'rejected' && v1Result.status === 'rejected') {
    throw v2Result.reason;
  }

  return agents;
}

/**
 * Get a single agent by ID, using the correct API path based on version.
 */
export async function getFoundryAgentByVersion(
  projectEndpoint: string,
  agentId: string,
  accessToken: string,
  agentVersion: FoundryAgentVersion = 'v2'
): Promise<FoundryAgent> {
  if (agentVersion === 'v1') {
    return getFoundryAssistant(projectEndpoint, agentId, accessToken);
  }
  return getFoundryAgent(projectEndpoint, agentId, accessToken);
}

/**
 * Update an agent using the correct API path based on version.
 */
export async function updateFoundryAgentByVersion(
  projectEndpoint: string,
  agentId: string,
  payload: UpdateAgentPayload,
  accessToken: string,
  agentVersion: FoundryAgentVersion = 'v2'
): Promise<FoundryAgent> {
  if (agentVersion === 'v1') {
    return updateFoundryAssistant(projectEndpoint, agentId, payload, accessToken);
  }
  return updateFoundryAgent(projectEndpoint, agentId, payload, accessToken);
}
